import {
  faChevronLeft,
  faChevronRight,
  faFileLines
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ReactElement } from 'react'
import { Paper } from '~/components/Global/Blocks/Paper/Paper'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { Button } from '~/components/Global/Interactions/Button/Button'
import { ButtonVariation } from '~/components/Global/Interactions/Button/ButtonVariation'
import { detectAttachmentFormat } from '~/domains/attachments/utils/formatDetector'
import type { PublicShortlistApplicant } from '../types'
import {
  viewer,
  viewerBody,
  viewerHeader,
  viewerImage,
  viewerNavigation,
  viewerNavigationButton,
  viewerTitle
} from './PublicShortlist.css'
import { ShortlistDocumentPreview } from './ShortlistDocumentPreview'
import { ShortlistPdfPreview } from './ShortlistPdfPreview'

type ShortlistFileViewerProps = {
  applicant: PublicShortlistApplicant
  position: number
  total: number
  onPrevious?: () => void
  onNext?: () => void
}

export const ShortlistFileViewer = ({
  applicant,
  position,
  total,
  onPrevious,
  onNext
}: ShortlistFileViewerProps): ReactElement => {
  const file = applicant.files.find((candidateFile) => candidateFile.previewUrl)
  const fullName = `${applicant.firstname} ${applicant.lastname}`
  const format = file ? detectAttachmentFormat(file.filename).format : null

  return (
    <Paper className={viewer} contentPadding={false}>
      <div className={viewerHeader}>
        <span className={viewerTitle}>CV de {fullName}</span>
        <div className={viewerNavigation}>
          <Button
            type="button"
            variation={ButtonVariation.Neutral}
            buttonSize="extraSmall"
            className={viewerNavigationButton}
            title="Profil précédent"
            aria-label="Profil précédent"
            disabled={!onPrevious}
            onClick={onPrevious}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </Button>
          {position} / {total}
          <Button
            type="button"
            variation={ButtonVariation.Neutral}
            buttonSize="extraSmall"
            className={viewerNavigationButton}
            title="Profil suivant"
            aria-label="Profil suivant"
            disabled={!onNext}
            onClick={onNext}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </Button>
        </div>
      </div>
      <div className={viewerBody}>
        {file?.previewUrl ? (
          format === 'pdf' ? (
            <ShortlistPdfPreview key={file.previewUrl} url={file.previewUrl} />
          ) : format === 'document' || format === 'sheet' ? (
            <ShortlistDocumentPreview
              key={file.previewUrl}
              url={file.previewUrl}
            />
          ) : (
            <img
              className={viewerImage}
              src={file.previewUrl}
              alt={`CV de ${fullName}`}
            />
          )
        ) : (
          <NoResult
            icon={faFileLines}
            message="Aucun CV à afficher pour ce profil."
            description={
              applicant.files.length > 0
                ? 'Ses fichiers sont disponibles en téléchargement.'
                : undefined
            }
          />
        )}
      </div>
    </Paper>
  )
}
